import { create } from 'zustand';

interface Business {
  id: number;
  name: string;
  address: string | null;
  rating: number | null;
  has_website: boolean;
  website: string | null;
  review_count?: number;
}

interface SearchFilters {
  no_website_only?: boolean;
  has_social_media?: boolean;
  minRating?: number;
  maxRating?: number;
  minReviewCount?: number;
  maxReviewCount?: number;
  minOpportunityScore?: number;
  maxOpportunityScore?: number;
  maxDistance?: number; // in miles
  [key: string]: any;
}

interface SearchState {
  results: Business[];
  query: string;
  location: string;
  filters: SearchFilters;
  hasSearched: boolean;
  setResults: (results: Business[]) => void;
  setSearchParams: (query: string, location: string, filters: SearchFilters) => void;
  clearResults: () => void;
  setHasSearched: (hasSearched: boolean) => void;
}

export const useSearchStore = create<SearchState>((set) => ({
  results: [],
  query: '',
  location: '',
  filters: {},
  hasSearched: false,
  setResults: (results) => {
    set({ results });
    // Persist to localStorage
    try {
      localStorage.setItem('search-results', JSON.stringify(results));
    } catch (e) {
      console.error('Failed to save search results', e);
    }
  },
  setSearchParams: (query, location, filters) => {
    set({ query, location, filters });
    // Persist to localStorage
    try {
      localStorage.setItem('search-params', JSON.stringify({ query, location, filters }));
    } catch (e) {
      console.error('Failed to save search params', e);
    }
  },
  clearResults: () => {
    set({ results: [], query: '', location: '', filters: {}, hasSearched: false });
    try {
      localStorage.removeItem('search-results');
      localStorage.removeItem('search-params');
    } catch (e) {
      console.error('Failed to clear search storage', e);
    }
  },
  setHasSearched: (hasSearched) => {
    set({ hasSearched });
    try {
      localStorage.setItem('search-has-searched', JSON.stringify(hasSearched));
    } catch (e) {
      console.error('Failed to save hasSearched', e);
    }
  },
}));

// Load from localStorage on initialization (only in browser)
if (typeof window !== 'undefined') {
  // Use a small delay to ensure store is initialized
  setTimeout(() => {
    try {
      const storedResults = localStorage.getItem('search-results');
      const storedParams = localStorage.getItem('search-params');
      const storedHasSearched = localStorage.getItem('search-has-searched');
      
      if (storedResults) {
        const results = JSON.parse(storedResults);
        useSearchStore.getState().setResults(results);
      }
      if (storedParams) {
        const { query, location, filters } = JSON.parse(storedParams);
        useSearchStore.getState().setSearchParams(query, location, filters);
      }
      if (storedHasSearched) {
        const hasSearched = JSON.parse(storedHasSearched);
        useSearchStore.getState().setHasSearched(hasSearched);
      }
    } catch (e) {
      console.error('Failed to load search state from localStorage', e);
    }
  }, 0);
}
